import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_TIMEOUT } from "utils";
import { saveAs } from "file-saver";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";

import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { leftJoin } from "utils/func";
import { getDatesWithoutWeekends } from "utils/func";

function DetailKinerjaPimpinanPD() {
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pegawaiInfo, setPegawaiInfo] = useState(null);
  const navigate = useNavigate();
  const { uid } = useParams();
  const [searchParams] = useSearchParams();
  const [listLibur, setListLibur] = useState([]);
  const [result, setResult] = useState([]);
  const [dataKu, setDataKu] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Ambil query parameters
  const tAwal = searchParams.get("tAwal");
  const tAkhir = searchParams.get("tAkhir");
  const nama = searchParams.get("nama");

  const fetchDetailData = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        swal({
          title: "Error!",
          text: "Data user tidak ditemukan",
          icon: "error",
          button: "OK",
        });
        return;
      }

      // Gunakan tanggal dari query parameters atau default
      let defaultDates;
      if (tAwal && tAkhir) {
        defaultDates = {
          tAwal: tAwal,
          tAkhir: tAkhir,
        };
      } else {
        // Fallback ke periode bulan berjalan
        // Set default tanggal dengan WITA (UTC+8)
        const today = new Date();
        const witaOffset = 8 * 60; // UTC+8 dalam menit
        const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

        const currentYear = witaDate.getFullYear();
        const currentMonth = witaDate.getMonth();
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);

        defaultDates = {
          tAwal: firstDayOfMonth.toISOString().split("T")[0],
          tAkhir: witaDate.toISOString().split("T")[0],
        };
      }

      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/getdh/mingguan",
        {
          tAwal: defaultDates.tAwal,
          tAkhir: defaultDates.tAkhir,
          idUser: uid,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success === 1) {
        setDetailData(response.data.data);
        if (response.data.data.length > 0) {
          setPegawaiInfo(response.data.data[0]);
        }
      } else {
        setDetailData([]);
        swal({
          title: "Info",
          text: "Tidak ada data detail kehadiran",
          icon: "info",
          button: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching detail data:", error);
      swal({
        title: "Error!",
        text: "Terjadi kesalahan saat mengambil data detail",
        icon: "error",
        button: "OK",
      });
      setDetailData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && uid) {
      fetchDetailData();
      getLibur();
    } else {
      logoutUser(navigate);
    }
  }, [navigate, uid, tAwal, tAkhir]);

  useEffect(() => {
    if (listLibur && detailData) {
      const data = { tAwal: tAwal, tAkhir: tAkhir, idUser: uid };

      const d = getDatesWithoutWeekends(tAwal, tAkhir, listLibur);

      const proses = leftJoin(d, detailData, "tanggal", "tanggal");
      setResult(proses);
      setDataKu(proses.data);
    }
  }, [listLibur, detailData]);

  const getLibur = useCallback(() => {
    axios({
      method: "get",
      url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
      timeout: API_TIMEOUT,
      headers: {
        Authorization: `Bearer ${""}`,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setListLibur(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching libur data:", error);
      });
  }, []);

  const formatWorkingHours = (workingHours) => {
    if (!workingHours || workingHours === "") {
      return "-";
    }
    return workingHours;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Hadir":
        return "success";
      case "Izin":
        return "warning";
      case "Sakit":
        return "error";
      case "Dinas Luar":
        return "info";
      case "Tugas Belajar":
        return "secondary";
      case "Cuti":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTanggal = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const columns = [
    {
      Header: "No",
      accessor: "no",
      width: "6%",
      align: "center",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Tanggal",
      accessor: "tanggal",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="text">
          {formatTanggal(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Hari",
      accessor: "hari",
      width: "12%",
      Cell: ({ row }) => formatDate(row.original.tanggal).split(",")[0],
    },
    {
      Header: "Status",
      accessor: "status",
      width: "10%",
      align: "center",
      Cell: ({ value }) => (
        <Chip
          label={value || "Tanpa Info"}
          color={getStatusColor(value)}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Waktu Masuk",
      accessor: "waktuMasuk",
      width: "10%",
      align: "center",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="text">
          {value || "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Waktu Pulang",
      accessor: "waktuPulang",
      width: "10%",
      align: "center",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="text">
          {value || "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Jam Kerja",
      accessor: "workingHours",
      width: "10%",
      align: "center",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="primary">
          {formatWorkingHours(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Persentase",
      accessor: "percentage",
      width: "12%",
      Cell: ({ row }) => {
        const percentage = calculatePercentage(row.original.workingHours);
        return (
          <MDBox>
            <MDBox display="flex" alignItems="center" mb={1}>
              <MDProgress
                value={percentage}
                color={
                  percentage >= 80
                    ? "success"
                    : percentage >= 60
                    ? "warning"
                    : "error"
                }
                variant="gradient"
                sx={{ flexGrow: 1, mr: 1 }}
              />
              <MDTypography variant="caption" fontWeight="bold" color="text">
                {percentage}%
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text.secondary">
              Target: 8 jam
            </MDTypography>
          </MDBox>
        );
      },
    },
  ];

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}`;
  };

  // Fungsi untuk menghitung persentase working hours
  const calculatePercentage = (workingHours) => {
    if (!workingHours || workingHours === "0:00" || workingHours === "") {
      return 0;
    }

    // Konversi workingHours ke menit
    const [hours, minutes] = workingHours.split(":").map(Number);
    const workingMinutes = hours * 60 + minutes;

    // Target jam kerja ideal (8 jam = 480 menit)
    const targetMinutes = 8 * 60; // 8 jam dalam menit

    if (targetMinutes === 0) return 0;

    const percentage = (workingMinutes / targetMinutes) * 100;
    return Math.round(percentage * 100) / 100; // Round to 2 decimal places
  };

  // Fungsi untuk menghitung statistik kinerja
  const calculatePerformanceStats = () => {
    const stats = {
      totalHari: dataKu.length,
      totalWorkingHours: 0,
      rataRataWorkingHours: 0,
      hariTerbaik: null,
      hariTerendah: null,
      totalHadir: 0,
      totalIzin: 0,
      totalSakit: 0,
      totalDinasLuar: 0,
      totalTugasBelajar: 0,
      totalCuti: 0,
      totalTanpaInfo: 0,
    };

    if (dataKu.length > 0) {
      // Hitung total working hours dan status
      dataKu.forEach((item) => {
        if (
          item.workingHours &&
          item.workingHours !== "0:00" &&
          item.workingHours !== ""
        ) {
          const [hours, minutes] = item.workingHours.split(":").map(Number);
          stats.totalWorkingHours += hours * 60 + minutes;
        }

        // Hitung status kehadiran
        switch (item.status) {
          case "Hadir":
            stats.totalHadir++;
            break;
          case "Izin":
            stats.totalIzin++;
            break;
          case "Sakit":
            stats.totalSakit++;
            break;
          case "Dinas Luar":
            stats.totalDinasLuar++;
            break;
          case "Tugas Belajar":
            stats.totalTugasBelajar++;
            break;
          case "Cuti":
            stats.totalCuti++;
            break;
          default:
            stats.totalTanpaInfo++;
            break;
        }
      });

      // Rata-rata working hours (hanya untuk hari yang hadir)
      if (stats.totalHadir > 0) {
        stats.rataRataWorkingHours = Math.round(
          stats.totalWorkingHours / stats.totalHadir
        );
      }

      // Hari dengan working hours tertinggi dan terendah
      const hariDenganJamKerja = dataKu
        .filter(
          (item) =>
            item.workingHours &&
            item.workingHours !== "0:00" &&
            item.workingHours !== ""
        )
        .sort((a, b) => {
          const getMinutes = (timeStr) => {
            if (!timeStr || timeStr === "0:00" || timeStr === "") return 0;
            const [hours, minutes] = timeStr.split(":").map(Number);
            return hours * 60 + minutes;
          };
          const minutesA = getMinutes(a.workingHours);
          const minutesB = getMinutes(b.workingHours);
          return minutesB - minutesA;
        });

      if (hariDenganJamKerja.length > 0) {
        stats.hariTerbaik = hariDenganJamKerja[0];
        stats.hariTerendah = hariDenganJamKerja[hariDenganJamKerja.length - 1];
      }
    }

    return stats;
  };

  const performanceStats = calculatePerformanceStats();

  const handleExportExcel = () => {
    if (!dataKu.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Tanggal",
      "Hari",
      "Status",
      "Waktu Masuk",
      "Waktu Pulang",
      "Jam Kerja",
      "Persentase (%)",
    ];

    let tableRows = "";
    dataKu.forEach((row, index) => {
      const percentage = calculatePercentage(row.workingHours);
      const hari = formatDate(row.tanggal).split(",")[0];
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${formatTanggal(row.tanggal)}</td>
          <td>${hari}</td>
          <td>${row.status || "Tanpa Info"}</td>
          <td>${row.waktuMasuk || "-"}</td>
          <td>${row.waktuPulang || "-"}</td>
          <td>${formatWorkingHours(row.workingHours)}</td>
          <td>${percentage}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Detail Kinerja Pimpinan PD - ${nama} (${tAwal} s/d ${tAkhir})</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Detail Kinerja Pimpinan PD: ${nama}</h2>
          <h3>Periode: ${formatTanggal(tAwal)} s/d ${formatTanggal(tAkhir)}</h3>
          <h4>Statistik Kehadiran:</h4>
          <ul>
            <li>Total Hari: ${performanceStats.totalHari}</li>
            <li>Total Jam Kerja: ${formatMinutesToHours(
              performanceStats.totalWorkingHours
            )}</li>
            <li>Hadir: ${performanceStats.totalHadir}</li>
            <li>Izin: ${performanceStats.totalIzin}</li>
            <li>Sakit: ${performanceStats.totalSakit}</li>
            <li>Dinas Luar: ${performanceStats.totalDinasLuar}</li>
            <li>Tugas Belajar: ${performanceStats.totalTugasBelajar}</li>
            <li>Cuti: ${performanceStats.totalCuti}</li>
            <li>Tanpa Info: ${performanceStats.totalTanpaInfo}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `detail-kinerja-pimpinan-pd-${nama}-${tAwal}-${tAkhir}.xls`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">
                Detail Kehadiran Pimpinan PD {nama}
              </MDTypography>
            </Grid>
            <Grid item>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleExportExcel}
                  disabled={!dataKu.length}
                >
                  <Icon>description</Icon>
                  &nbsp;Export Excel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate(-1)}
                >
                  <Icon>arrow_back</Icon>&nbsp;Kembali
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Statistik Kehadiran */}
        <MDBox mb={3}>
          <MDTypography variant="h6" color="text" mb={2}>
            Statistik Kehadiran
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                icon={<Icon>calendar_today</Icon>}
                label={`Total Hari: ${performanceStats.totalHari}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>schedule</Icon>}
                label={`Total Jam Kerja: ${formatMinutesToHours(
                  performanceStats.totalWorkingHours
                )}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>check_circle</Icon>}
                label={`Hadir: ${performanceStats.totalHadir}`}
                color="success"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>event_busy</Icon>}
                label={`Izin: ${performanceStats.totalIzin}`}
                color="warning"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>local_hospital</Icon>}
                label={`Sakit: ${performanceStats.totalSakit}`}
                color="error"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>business_center</Icon>}
                label={`Dinas Luar: ${performanceStats.totalDinasLuar}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>school</Icon>}
                label={`Tugas Belajar: ${performanceStats.totalTugasBelajar}`}
                color="secondary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>beach_access</Icon>}
                label={`Cuti: ${performanceStats.totalCuti}`}
                color="default"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>help_outline</Icon>}
                label={`Tanpa Info: ${performanceStats.totalTanpaInfo}`}
                color="default"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="h6" color="text">
            Detail Kehadiran Harian: {formatTanggal(tAwal)} s/d{" "}
            {formatTanggal(tAkhir)}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Total: {dataKu.length} hari (Diurutkan berdasarkan tanggal)
          </MDTypography>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading && (
              <MDTypography variant="button" color="text.secondary">
                Memuat...
              </MDTypography>
            )}
            {!loading &&
              dataKu.map((row, index) => {
                const percentage = calculatePercentage(row.workingHours);
                const hari = formatDate(row.tanggal).split(",")[0];
                return (
                  <Card key={`${row.tanggal || "row"}-${index}`}>
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <MDTypography variant="button" fontWeight="bold">
                            {index + 1}. {formatTanggal(row.tanggal)} ({hari})
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox
                            mt={0.5}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Chip
                              label={row.status || "Tanpa Info"}
                              color={getStatusColor(row.status)}
                              size="small"
                            />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Status
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Masuk
                          </MDTypography>
                          <MDTypography variant="button">
                            {row.waktuMasuk || "-"}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Pulang
                          </MDTypography>
                          <MDTypography variant="button">
                            {row.waktuPulang || "-"}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Jam Kerja
                          </MDTypography>
                          <MDTypography variant="button">
                            {formatWorkingHours(row.workingHours)}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox display="flex" alignItems="center" mb={0.5}>
                            <MDProgress
                              value={percentage}
                              color={
                                percentage >= 80
                                  ? "success"
                                  : percentage >= 60
                                  ? "warning"
                                  : "error"
                              }
                              variant="gradient"
                              sx={{ flexGrow: 1, mr: 1 }}
                            />
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color="text"
                            >
                              {percentage}%
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Target: 8 jam
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
          </MDBox>
        ) : (
          <DataTable
            table={{
              columns,
              rows: dataKu,
            }}
            entriesPerPage={false}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            loading={loading}
          />
        )}

        {dataKu.length === 0 && !loading && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
              assessment
            </Icon>
            <MDTypography variant="h6" color="text.secondary">
              Tidak ada data detail kehadiran
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Coba periksa kembali periode yang dipilih
            </MDTypography>
          </MDBox>
        )}

        {loading && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <MDTypography variant="h6" color="text.secondary">
              Memuat data...
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default DetailKinerjaPimpinanPD;

